/**
 * Freshness → UI copy. Pure mapping, no React/store imports — the single
 * place that turns the store's honesty state into the words AgentSpace
 * puts on hover labels and the field's floor inscription.
 *
 * THREE STATES, NOT TWO. The site may say "real" only once it has verified
 * that itself, and may say "simulation" only once it knows the observer is
 * unreachable. Before either is established — the first paint of every page,
 * every server-rendered byte a crawler reads, every session where the SSE
 * stream never arrives — the honest answer is "not established yet", which
 * is what `SYNCING` means here. `displayFreshness` therefore never invents
 * an OFFLINE verdict out of an unknown one:
 *
 *   rosterLive          → the stream's own freshness, unchanged. A roster
 *                         that later goes STALE/OFFLINE stops reading LIVE.
 *   !rosterLive OFFLINE → OFFLINE. The site tried and failed; what is on
 *                         screen is the static fallback and says so.
 *   !rosterLive other   → SYNCING. Nothing is claimed in either direction:
 *                         not real, not simulated, not yet known.
 *
 * The second rule is what keeps a server-rendered footer from flatly denying
 * a LIVE badge rendered two hundred pixels above it.
 *
 * `mode` is folded in wherever a surface would otherwise present an
 * engineering canary as the flagship universe: a canary is observer-backed
 * and real, but its decisions may come from the engine's deterministic code
 * fallback rather than a model, so it never reads as plain LIVE.
 */

import type { AgentCognition, Freshness, LiveMode } from './types.ts';

export function displayFreshness(rosterLive: boolean, freshness: Freshness): Freshness {
  if (rosterLive) return freshness;
  return freshness === 'OFFLINE' ? 'OFFLINE' : 'SYNCING';
}

const BASE_SUFFIX: Record<Freshness, string> = {
  LIVE: '· LIVE',
  STALE: '· STALE',
  SYNCING: '· SYNCING',
  OFFLINE: '· OFFLINE · SIM',
};

/**
 * True when the canary distinction is a live fact worth stating: the run is
 * observer-backed (so there IS a run) and it is not the flagship universe.
 * OFFLINE has no run at all, so a mode marker there would claim more than is
 * known.
 */
export function showsCanary(freshness: Freshness, mode?: LiveMode | null): boolean {
  return mode === 'canary' && freshness !== 'OFFLINE';
}

/** Compact machine suffix: `· LIVE`, `· LIVE · CANARY`, `· OFFLINE · SIM`, … */
export function freshnessSuffix(freshness: Freshness, mode?: LiveMode | null): string {
  const base = BASE_SUFFIX[freshness];
  return showsCanary(freshness, mode) ? `${base} · CANARY` : base;
}

/**
 * Hover-label suffix for one agent: the field's honest freshness (plus the
 * canary marker), and a `· code-fallback` marker for rows whose last recorded
 * decision came from the engine's deterministic fallback policy, not a model.
 */
export function agentLabelSuffix(
  freshness: Freshness,
  cognition?: AgentCognition,
  mode?: LiveMode | null,
): string {
  const base = freshnessSuffix(freshness, mode);
  return cognition === 'code-fallback' ? `${base} · code-fallback` : base;
}

/**
 * The inscription written across the floor of the agent field. It states the
 * source of the population before it states its freshness, because the
 * source is the part a visitor cannot infer from the glyphs.
 *
 * `shown`/`population` disclose the render cap: the 3D field draws a bounded
 * number of agents, and a field that silently draws 120 of 2000 would be
 * under-reporting the universe. Passing them is optional; when `shown` is
 * below `population` the inscription says so out loud.
 */
export function floorInscription(
  freshness: Freshness,
  mode?: LiveMode | null,
  shown?: number,
  population?: number,
): string {
  const cap = fieldCapNote(shown, population);
  if (freshness === 'OFFLINE') return `AGENT FIELD — AMBIENT SIMULATION${cap}`;
  if (freshness === 'SYNCING' && mode == null) {
    return `AGENT FIELD — SOURCE NOT YET ESTABLISHED ${BASE_SUFFIX.SYNCING}${cap}`;
  }
  if (showsCanary(freshness, mode)) {
    return `AGENT FIELD — ENGINEERING CANARY · CODE FALLBACK POSSIBLE ${BASE_SUFFIX[freshness]}${cap}`;
  }
  return `AGENT FIELD ${BASE_SUFFIX[freshness]}${cap}`;
}

/** ` · SHOWING 120 OF 2000` when the field draws fewer rows than the roster holds. */
export function fieldCapNote(shown?: number, population?: number): string {
  if (
    typeof shown !== 'number' ||
    typeof population !== 'number' ||
    !Number.isFinite(shown) ||
    !Number.isFinite(population) ||
    shown >= population
  ) {
    return '';
  }
  return ` · SHOWING ${shown} OF ${population}`;
}
